import 'yet-another-react-lightbox/styles.css'

import { LoadSpinner } from '@calizahq/react-components-v2'
import React, { FC, useState } from 'react'
import Lightbox from 'yet-another-react-lightbox'

import { TicketQueries } from '@/graphql/queries/ticket.queries'
import { TicketCommentQueries } from '@/graphql/queries/ticketComment.queries'
import { useGraphql } from '@/hooks/useGraphql'
import { Attachment } from '@/types/Attachment'
import { Status, TicketDetail } from '@/types/Ticket'
import { TicketComment } from '@/types/TicketComment'

import { CreateAttachment } from './CreateAttachment'

type Props = {
  ticketData: TicketDetail
}
export const MediaSection: FC<Props> = ({ ticketData }) => {
  const { data, isLoading } = useGraphql<{ ticketComments: null | TicketComment[] }>(
    TicketCommentQueries.QUERY_KEY,
    TicketCommentQueries.getTicketComments(),
    { filter: { ticketId: ticketData.id } },
  )

  const { refetch } = useGraphql<{ ticket: TicketDetail }>(
    TicketQueries.QUERY_KEYS.getTicketDetail,
    TicketQueries.getTicketDetail(),
    { id: ticketData.id },
  )

  const attachments = ticketData.attachments || []
  const [photoIndex, setPhotoIndex] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const isTicketClosed = ticketData.internalTicketStatus === Status.CLOSED

  const ticketImages: Attachment[] = []
  const ticketFiles: Attachment[] = []
  const ticketCommentsData = data?.ticketComments || []

  attachments.forEach((attachment, _) => {
    if (attachment.contentType.includes('image')) {
      ticketImages.push(attachment)
    } else {
      ticketFiles.push(attachment)
    }
  })

  let commentImages: Attachment[] = []
  let commentFiles: Attachment[] = []

  const collectAttachments = (attachmentList: null | Attachment[]) => {
    const _attachments = attachmentList || []
    if (_attachments.length) {
      const computedAttachments: [Attachment[], Attachment[]] = [[], []]
      const [images, files] = _attachments.reduce(([_images, _files], el) => {
        return el.contentType.includes('image') ? [[..._images, el], _files] : [_images, [..._files, el]]
      }, computedAttachments)
      commentImages = [...commentImages, ...images]
      commentFiles = [...commentFiles, ...files]
    }
  }

  ticketCommentsData.forEach((comment) => {
    collectAttachments(comment.attachments)
    comment.childComments?.forEach((child) => {
      collectAttachments(child.attachments)
    })
  })

  const images = [...ticketImages, ...commentImages]
  const files = [...ticketFiles, ...commentFiles]

  const getFileName = (fileName: string = '') => {
    const paths = fileName.split('/')
    return paths.pop()
  }

  const showImage = (index: number) => {
    setPhotoIndex(index)
    setIsOpen(true)
  }

  return (
    <div>
      <div className=" my-4 flex  min-h-[70vh] w-full flex-col">
        {isLoading ? (
          <div className="flex h-48 w-full items-center justify-center ">
            <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
          </div>
        ) : (
          <>
            <div className="mb-8">
              <p className="font-weight[700] mb-4 " style={{ fontWeight: 700 }}>
                Images
              </p>
              <div className="grid grid-cols-2 place-items-stretch gap-x-8 gap-y-8 md:grid-cols-4">
                {images.map((file, i) => {
                  return (
                    <img
                      alt={file.filename}
                      aria-hidden="true"
                      className="max-h-48 w-64 overflow-hidden rounded-lg"
                      key={i}
                      onClick={() => showImage(i)}
                      src={`/${file.path}`}
                    />
                  )
                })}
              </div>
            </div>
            <div className="mb-8">
              <p className="font-weight[700] mb-4 " style={{ fontWeight: 700 }}>
                Documents
              </p>
              <div className="flex flex-wrap">
                {files.map((file) => (
                  <a
                    className="mb-1 mr-2 rounded-full bg-grey-100 px-3 py-2 text-p-xs underline"
                    href={`/${file.path}`}
                    key={file.id}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {getFileName(file.filename)}
                  </a>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      <div>
        <Lightbox
          close={() => setIsOpen(false)}
          index={photoIndex}
          open={isOpen}
          slides={images.map((img) => ({ src: `/${img.path}` }))}
        />
      </div>
      {!isTicketClosed && <CreateAttachment refetchOnSuccess={refetch} ticketId={ticketData.id} />}
    </div>
  )
}
